import { useCallback } from 'react';
import { EntityId } from '@reduxjs/toolkit';
import { DialogsFilterType } from '@common/model/dialog/filter';
import { UnreadMessagesCountResponse } from '@common/model/dialog/unreadMessagesCount.response';
import { MemberUser } from '@youhelp-ru/core';
import { useIsLoggedIn } from '../../currentUser';
import { coreDialogApi } from '../api';

export interface UnreadMessagesCountQueryProps {
  filter?: DialogsFilterType;
  as?: MemberUser;
  isInitiator?: boolean;
}

/** @deprecated use {@link UnreadMessagesCountQueryProps} */
export type UseUnreadMessagesCountProps = UnreadMessagesCountQueryProps;

export const useUnreadMessagesCount = (props: UnreadMessagesCountQueryProps = {}): number => {
  const isLoggedIn = useIsLoggedIn();
  const { data } = coreDialogApi.endpoints.getUnreadMessagesCount.useQuery(
    { filter: props.filter, as: props.as, isInitiator: props.isInitiator },
    { skip: !isLoggedIn },
  );
  return data?.count ?? 0;
};

/**
 * Unread messages counter for a single dialog.
 *
 * The counter is read straight from the cache entry of the very same
 * `getUnreadMessagesCount` request the list is built upon: the per-dialog map is scoped to its
 * query arguments, so responses of the other argument combinations (filter tabs, other members)
 * can not overwrite it.
 */
export const useUnreadMessagesCountInDialog = (dialogId: EntityId, props: UnreadMessagesCountQueryProps = {}): number => {
  const isLoggedIn = useIsLoggedIn();
  const selectFromResult = useCallback(
    ({ data }: { data?: UnreadMessagesCountResponse }) => ({ count: data?.dialogs[dialogId] ?? 0 }),
    [dialogId],
  );
  const { count } = coreDialogApi.endpoints.getUnreadMessagesCount.useQuery(
    { filter: props.filter, as: props.as, isInitiator: props.isInitiator },
    { skip: !isLoggedIn, selectFromResult },
  );
  return count;
};
